import React, { useState, useEffect } from 'react';
import './App.css';
import BitcoinPriceChart from './components/BitcoinPriceChart';
import BTCTreasuryScorer from './components/BTCTreasuryScorer';
import BitcoinTreasuryFeed from './components/BitcoinTreasuryFeed';
import { AuthProvider } from './AuthContext';
import AuthButton from './components/AuthButton';

function App() {
  const [isAppReady, setIsAppReady] = useState(false);
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    // Cache busting and version check
    const checkCacheAndVersion = () => {
      console.log('🔄 Cache-busting check initiated');
      
      // Log build info for debugging
      const buildTime = process.env.REACT_APP_BUILD_TIME || 'unknown';
      const version = process.env.REACT_APP_VERSION || 'unknown';
      console.log(`📦 App Build Time: ${buildTime}, Version: ${version}`);
      
      // Clear any stale caches on startup
      if ('caches' in window) {
        caches.keys().then(function(names) {
          names.forEach(name => {
            if (name.includes('workbox') || name.includes('precache')) {
              console.log('🗑️ Clearing cache:', name);
              caches.delete(name);
            }
          });
        });
      }
      
      // Force fresh load detection
      const lastVersion = localStorage.getItem('app_version');
      if (lastVersion && lastVersion !== version && version !== 'unknown') {
        console.log('🆕 New version detected, clearing app cache');
        localStorage.clear();
        sessionStorage.clear();
      }
      localStorage.setItem('app_version', version);
    };
    
    checkCacheAndVersion();
    
    // Add a small delay to ensure DOM is fully ready (Safari issue)
    const timer = setTimeout(() => {
      console.log('🚀 App initialization complete');
      setIsAppReady(true);
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  // Retry mechanism for Safari
  const handleAppError = () => {
    if (retryCount < 3) {
      console.log(`🔄 Retrying app initialization (attempt ${retryCount + 1})`);
      setRetryCount(prev => prev + 1);
      setIsAppReady(false);
      
      setTimeout(() => {
        setIsAppReady(true);
      }, 1000);
    }
  };

  if (!isAppReady) {
    return (
      <div className="App min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-white text-lg">Loading Crypto Dashboard...</p>
          <p className="text-gray-400 text-sm mt-2">Initializing components</p>
        </div>
      </div>
    );
  }

  return (
    <AuthProvider>
      <div className="App min-h-screen bg-black">
        <div className="container mx-auto px-4 py-8 space-y-8">
          {/* Header Section */}
          <header className="text-center mb-8">
            <div className="flex justify-between items-center mb-6">
              <h1 className="text-2xl sm:text-4xl font-bold text-white">
                Bitcoin Treasury Investment Tracker
              </h1>
              <AuthButton />
            </div>
          <p className="text-gray-300 text-lg mb-6">
            Real-time analysis of MSTR, TSLA, MARA, RIOT and corporate Bitcoin strategies
          </p>
          
          {/* Main SEO & Value Proposition */}
          <div className="bg-gradient-to-r from-blue-900/50 to-purple-900/50 rounded-lg p-6 max-w-5xl mx-auto mb-8 text-left border border-blue-700/30">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold text-white mb-3">🎯 Why Track Bitcoin Treasury Companies?</h2>
              <p className="text-gray-300 text-lg max-w-3xl mx-auto">
                Companies like <strong className="text-yellow-400">MicroStrategy (MSTR)</strong> with 190,000+ BTC holdings often see their stock prices correlate with Bitcoin movements. Our dashboard helps investors identify these relationships and discover new Bitcoin treasury opportunities.
              </p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-6 text-sm text-gray-300 mb-6">
              <div className="bg-gray-800/50 rounded-lg p-4">
                <h3 className="font-semibold text-blue-400 mb-3 flex items-center">
                  📊 Live Stock Analysis
                </h3>
                <p className="mb-3">Track real-time stock performance of major Bitcoin treasury holders:</p>
                <ul className="text-xs space-y-1 text-gray-400">
                  <li>• <strong className="text-orange-400">MicroStrategy (MSTR)</strong> - 190,000+ BTC</li>
                  <li>• <strong className="text-red-400">Tesla (TSLA)</strong> - 9,720+ BTC</li>
                  <li>• <strong className="text-green-400">Marathon Digital (MARA)</strong> - Mining + Treasury</li>
                  <li>• <strong className="text-purple-400">Riot Platforms (RIOT)</strong> - Mining Operations</li>
                  <li>• <strong className="text-blue-400">Coinbase (COIN)</strong> - Exchange + Holdings</li>
                </ul>
              </div>
              
              <div className="bg-gray-800/50 rounded-lg p-4">
                <h3 className="font-semibold text-green-400 mb-3 flex items-center">
                  ⚡ Treasury Scoring System
                </h3>
                <p className="mb-3">Rate any company's Bitcoin treasury potential (0-100 score):</p>
                <ul className="text-xs space-y-1 text-gray-400">
                  <li>• Financial strength analysis</li>
                  <li>• Cash flow assessment</li>
                  <li>• Market cap evaluation</li>
                  <li>• Volatility tolerance</li>
                  <li>• CEO crypto sentiment</li>
                  <li>• Corporate governance factors</li>
                </ul>
              </div>
              
              <div className="bg-gray-800/50 rounded-lg p-4">
                <h3 className="font-semibold text-yellow-400 mb-3 flex items-center">
                  🚨 Early Detection System
                </h3>
                <p className="mb-3">Discover new Bitcoin adopters before they become mainstream:</p>
                <ul className="text-xs space-y-1 text-gray-400">
                  <li>• Treasury announcement alerts</li>
                  <li>• Corporate Bitcoin news feed</li>
                  <li>• SEC filing analysis</li>
                  <li>• Executive statement tracking</li>
                  <li>• Strategic move predictions</li>
                  <li>• Investment opportunity identification</li>
                </ul>
              </div>
            </div>
            
            <div className="bg-gray-900/50 rounded-lg p-4 border border-gray-700">
              <h3 className="font-semibold text-white mb-2 text-center">📈 Bitcoin-Stock Price Correlation Insights</h3>
              <p className="text-sm text-gray-300 text-center mb-3">
                Studies show Bitcoin treasury companies often experience <strong className="text-green-400">amplified price movements</strong> during Bitcoin bull runs. 
                Our dashboard helps identify these correlation patterns and investment opportunities.
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs text-center">
                <div>
                  <div className="text-orange-400 font-bold">MSTR</div>
                  <div className="text-gray-400">~2-3x BTC correlation</div>
                </div>
                <div>
                  <div className="text-green-400 font-bold">MARA</div>
                  <div className="text-gray-400">~1.5-2x BTC correlation</div>
                </div>
                <div>
                  <div className="text-purple-400 font-bold">RIOT</div>
                  <div className="text-gray-400">~1.5-2x BTC correlation</div>
                </div>
                <div>
                  <div className="text-red-400 font-bold">TSLA</div>
                  <div className="text-gray-400">~0.5-1x BTC correlation</div>
                </div>
              </div>
            </div>
            
            <div className="mt-4 pt-4 border-t border-gray-700">
              <p className="text-xs text-gray-400 text-center">
                <strong>Data Sources:</strong> Real-time APIs (Polygon.io, Twelve Data, Finnhub, Alpha Vantage) • 
                News feeds (CoinDesk, Bitcoin Magazine, Decrypt, CoinTelegraph) • 
                Updated every 15 minutes • Educational purposes only, not financial advice
              </p>
            </div>
          </div>
        </header>
        
        {/* Error boundary for Safari issues */}
        <div className="space-y-8">
          <div key={`chart-${retryCount}`}>
            <BitcoinPriceChart onError={handleAppError} />
          </div>
          <div key={`scorer-${retryCount}`}>
            <BTCTreasuryScorer />
          </div>
          <div key={`feed-${retryCount}`}>
            <BitcoinTreasuryFeed onError={handleAppError} />
          </div>
        </div>
        
        {retryCount > 0 && (
          <div className="text-center mt-4">
            <p className="text-yellow-400 text-sm">
              Recovery attempt {retryCount}/3 - Some features may take longer to load on mobile
            </p>
          </div>
        )}

        {/* Enhanced Footer with SEO Content */}
        <footer className="bg-gradient-to-r from-gray-900 to-gray-800 rounded-lg p-8 mt-12 border border-gray-700">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 text-sm">
            <div>
              <h3 className="font-bold text-blue-400 mb-3">📊 Top Bitcoin Treasury Stocks</h3>
              <div className="space-y-2 text-gray-300">
                <div className="flex justify-between">
                  <span className="text-orange-400 font-semibold">MSTR</span>
                  <span className="text-xs text-gray-400">190,000+ BTC</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-red-400 font-semibold">TSLA</span>
                  <span className="text-xs text-gray-400">9,720+ BTC</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-green-400 font-semibold">MARA</span>
                  <span className="text-xs text-gray-400">Mining + Treasury</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-purple-400 font-semibold">RIOT</span>
                  <span className="text-xs text-gray-400">Mining Operations</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-blue-400 font-semibold">COIN</span>
                  <span className="text-xs text-gray-400">Exchange Holdings</span>
                </div>
              </div>
            </div>
            
            <div>
              <h3 className="font-bold text-green-400 mb-3">💡 Investment Strategy Benefits</h3>
              <ul className="text-gray-400 space-y-1 text-xs">
                <li>• <strong className="text-gray-300">Inflation hedge</strong> through Bitcoin exposure</li>
                <li>• <strong className="text-gray-300">Portfolio diversification</strong> into digital assets</li>
                <li>• <strong className="text-gray-300">Growth potential</strong> during Bitcoin bull markets</li>
                <li>• <strong className="text-gray-300">Corporate innovation</strong> and forward-thinking leadership</li>
                <li>• <strong className="text-gray-300">Currency debasement</strong> protection strategy</li>
                <li>• <strong className="text-gray-300">Digital transformation</strong> positioning</li>
              </ul>
            </div>
            
            <div>
              <h3 className="font-bold text-yellow-400 mb-3">🎯 How to Use This Tracker</h3>
              <ul className="text-gray-400 space-y-1 text-xs">
                <li>• <strong className="text-gray-300">Monitor correlations</strong> between BTC price and stock movements</li>
                <li>• <strong className="text-gray-300">Score companies</strong> for treasury adoption potential</li>
                <li>• <strong className="text-gray-300">Track news</strong> for early investment signals</li>
                <li>• <strong className="text-gray-300">Compare performance</strong> across treasury holdings</li>
                <li>• <strong className="text-gray-300">Identify opportunities</strong> before mainstream adoption</li>
                <li>• <strong className="text-gray-300">Research fundamentals</strong> for informed decisions</li>
              </ul>
            </div>
            
            <div>
              <h3 className="font-bold text-white mb-3">📈 Market Data & Sources</h3>
              <div className="text-gray-400 text-xs space-y-2">
                <div>
                  <strong className="text-gray-300">Live Price APIs:</strong><br/>
                  Polygon.io • Twelve Data • Finnhub • Alpha Vantage
                </div>
                <div>
                  <strong className="text-gray-300">News Sources:</strong><br/>
                  CoinDesk • Bitcoin Magazine • Decrypt • CoinTelegraph
                </div>
                <div>
                  <strong className="text-gray-300">Update Frequency:</strong><br/>
                  Stock prices: Every 3 minutes<br/>
                  News feed: Every 15 minutes
                </div>
              </div>
            </div>
          </div>
          
          <div className="mt-8 pt-4 border-t border-gray-600">
            <div className="text-center">
              <h4 className="font-bold text-white mb-2">🚀 Start Your Bitcoin Treasury Investment Journey</h4>
              <p className="text-sm text-gray-300 mb-3 max-w-3xl mx-auto">
                Join thousands of investors tracking Bitcoin treasury companies. Use our real-time data, scoring system, and news alerts to make informed investment decisions in the growing corporate Bitcoin adoption space.
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center text-xs">
                <div>
                  <div className="text-blue-400 font-bold">Real-Time Data</div>
                  <div className="text-gray-500">Live stock prices</div>
                </div>
                <div>
                  <div className="text-green-400 font-bold">Treasury Scoring</div>
                  <div className="text-gray-500">0-100 rating system</div>
                </div>
                <div>
                  <div className="text-yellow-400 font-bold">News Alerts</div>
                  <div className="text-gray-500">Early adoption signals</div>
                </div>
                <div>
                  <div className="text-purple-400 font-bold">Market Analysis</div>
                  <div className="text-gray-500">Correlation insights</div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="mt-6 pt-4 border-t border-gray-700 text-center">
            <p className="text-xs text-gray-500">
              <strong>Bitcoin Treasury Investment Tracker</strong> • Monitor MSTR, TSLA, MARA, RIOT, COIN Bitcoin strategies • 
              Educational analysis tool for informed investment research • Not financial advice • 
              <span className="text-blue-400">Data updated every 15 minutes</span>
            </p>
          </div>
        </footer>
      </div>
    </div>
    </AuthProvider>
  );
}

export default App;
